<script setup lang="ts">
import { reorderByIds } from "core/helpers";
import { formatCurrency } from "shared/helpers";
import { capitalize } from "vue";

import type { ShopProductResponseDto } from "~/common/interfaces/dto/shop/shop-product-response.dto";
import type { MaterialTrimmed } from "~/common/interfaces/material-trimmed";
import type { SizeTrimmed } from "~/common/interfaces/size-trimmed";

const props = defineProps({
	product: {
		type: Object as PropType<ShopProductResponseDto>,
		required: true
	},
	size: {
		type: Object as PropType<SizeTrimmed>,
		required: true
	},
	material: {
		type: Object as PropType<MaterialTrimmed>,
		required: true
	},
	useCustomText: {
		type: Boolean,
		required: true
	},
	text: {
		type: String,
		required: true
	},
	errors: {
		type: Object,
		required: true
	}
});

interface Emits {
	(event: "update:size", size: SizeTrimmed): void
	(event: "update:material", size: MaterialTrimmed): void
	(event: "update:useCustomText", value: boolean): void
	(event: "update:text", text: string): void
}
const emit = defineEmits<Emits>();

const updateSize = (size: SizeTrimmed) => {
	emit("update:size", size);
};
const updateMaterial = (material: MaterialTrimmed) => {
	emit("update:material", material);
};
const updateUseCustomText = (value: boolean) => {
	emit("update:useCustomText", value);
};
const updateText = (text: string) => {
	emit("update:text", text);
};

watch(() => props.useCustomText, (value) => {
	if (!value) {
		updateText("");
	}
});
</script>

<template>
	<div>
		<div v-if="product.feature.useCustomText" class="mb-8">
			<AppCheckbox
				:model-value="useCustomText"
				:title="`${$t('addCustomText')} (${product.feature.customTextPrice ? `+${formatCurrency(product.feature.customTextPrice)}` : $t('free')})`"
				@update:model-value="updateUseCustomText"
			/>
			<AppInput
				class="mt-5"
				:title="capitalize($t('inputCustomTextPlaceholder'))"
				:disabled="!useCustomText"
				:model-value="text"
				:error="errors.text"
				@update:model-value="updateText"
			/>
		</div>
		<div class="mb-5 flex items-center gap-x-8">
			<span class="uppercase font-medium text-sm">{{ $t('size') }} (cm)</span>
			<ProductDetailsCustomizerSize
				:sizes="product.sizes"
				@change:size="updateSize"
			/>
		</div>
		<div class="flex items-center gap-x-8">
			<span class="uppercase font-medium text-sm">{{ $t('material') }}</span>
			<ProductDetailsCustomizerMaterial
				:materials="reorderByIds(product.materials, product.feature.materialOrderIds)"
				@change:material="updateMaterial"
			/>
		</div>
	</div>
</template>
